import { handleActions } from "redux-actions";
import { axios } from "../cores/axiosWrapper";
import Polling from "../cores/polling";

export const listRegisteredDomainsAPI = (apiEndpoint, projectId, params) => {
    return axios.get(`${apiEndpoint}/v3/integrated-search/domains`, { params, headers: { projectId } });
};

const registerDomainAPI = (apiEndpoint, projectId, domain) => {
    return axios.post(`${apiEndpoint}/v3/integrated-search/domains`, { domain }, { headers: { projectId } });
};

const unregisterDomainAPI = (apiEndpoint, projectId, domainId) => {
    return axios.delete(`${apiEndpoint}/v3/integrated-search/domains/${domainId}`, { headers: { projectId } });
};

export const searchAPI = (apiEndpoint, projectId, body) => {
    return axios.post(`${apiEndpoint}/v3/integrated-search/search`, body, { headers: { projectId } });
};

export const AI_SEARCH_ACTIVE_STATES = ["QUEUED", "SYNCING", "COMPLETED", "PARTIAL_COMPLETED"];
export const AI_SEARCH_SETTLED_STATES = ["COMPLETED", "PARTIAL_COMPLETED"];

export const isAiSearchDomainEnabled = (domains) =>
    !!domains?.some((domain) => AI_SEARCH_ACTIVE_STATES.includes(domain?.state));

export const isAiSearchDomainSettled = (domains) =>
    !domains?.length || domains.every((domain) => AI_SEARCH_SETTLED_STATES.includes(domain?.state));

const GET_LIST_REGISTERED_DOMAIN_PENDING_V3 = "GET_LIST_REGISTERED_DOMAIN_PENDING_V3";
const GET_LIST_REGISTERED_DOMAIN_SUCCESS_V3 = "GET_LIST_REGISTERED_DOMAIN_SUCCESS_V3";
const GET_LIST_REGISTERED_DOMAIN_FAILURE_V3 = "GET_LIST_REGISTERED_DOMAIN_FAILURE_V3";
const ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V3 = "ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V3";
const ADD_LIST_REGISTERED_DOMAIN_FAILURE_V3 = "ADD_LIST_REGISTERED_DOMAIN_FAILURE_V3";
const DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V3 = "DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V3";
const DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V3 = "DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V3";

const initialState = {
    // pending 초기값 true: 첫 조회 완료 전엔 "조회 중"으로 간주(목록 페이지의 AI 모드 페이지 확정/패치를 보류)
    registeredDomain: {
        ASSET: { domains: [], pending: true, error: null },
    },
};

export default handleActions(
    {
        [GET_LIST_REGISTERED_DOMAIN_PENDING_V3]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        // 이전 domains를 보존: 재조회/폴링 재시작 시 PENDING이 목록을 비워 AI 모드 버튼이 깜빡이지 않도록
                        ...(state.registeredDomain[action.payload.domain] ?? { domains: [] }),
                        pending: true,
                        error: null,
                    },
                },
            };
        },
        [GET_LIST_REGISTERED_DOMAIN_SUCCESS_V3]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: { domains: action.payload.data, pending: false },
                },
            };
        },
        [GET_LIST_REGISTERED_DOMAIN_FAILURE_V3]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        // 이전 domains를 보존: 실패로 domains가 undefined가 되면 이후 DELETE_SUCCESS의 filter()가 터짐
                        ...(state.registeredDomain[action.payload.domain] ?? { domains: [] }),
                        error: action.payload.data,
                        pending: false,
                    },
                },
            };
        },
        [ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V3]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        domains: [
                            ...(state.registeredDomain[action.payload.domain]?.domains ?? []),
                            action.payload.data,
                        ],
                        pending: false,
                    },
                },
            };
        },
        [ADD_LIST_REGISTERED_DOMAIN_FAILURE_V3]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        // 이전 domains를 보존: 실패로 domains가 undefined가 되면 이후 DELETE_SUCCESS의 filter()가 터짐
                        ...(state.registeredDomain[action.payload.domain] ?? { domains: [] }),
                        error: action.payload.data,
                        pending: false,
                    },
                },
            };
        },
        [DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V3]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        domains:
                            state.registeredDomain[action.payload.domain]?.domains?.filter(
                                (domain) => domain.id !== action.payload.data,
                            ) ?? [],
                        pending: false,
                    },
                },
            };
        },
        [DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V3]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        ...state.registeredDomain[action.payload.domain],
                        pending: false,
                    },
                },
            };
        },
    },
    initialState,
);

const SETTLE_POLL_INTERVAL_MS = 5000;
const domainPolling = {}; // { [domain]: { instance, cancelled } }

export const stopRegisteredDomainsPolling = (domain) => () => {
    const entry = domainPolling[domain];
    if (entry) {
        entry.cancelled = true; // 진행 중 tick의 늦은 응답을 dispatch 단계에서 드롭(덮어쓰기 방지)
        if (entry.instance?.timer) clearTimeout(entry.instance.timer);
    }
    domainPolling[domain] = null;
};

// SUCCESS만 디스패치해 V3 슬라이스만 갱신하므로 parseAssetSettings(다른 edit 값)에 영향이 없다.
export const pollRegisteredDomainsUntilSettled = (domain, externalId) => (dispatch, getState) => {
    const { stage, project } = getState();

    dispatch(stopRegisteredDomainsPolling(domain));
    const entry = { instance: null, cancelled: false };
    domainPolling[domain] = entry;

    entry.instance = Polling(
        () => listRegisteredDomainsAPI(stage.endpoint, project.id, { domain, externalId }),
        (response) => entry.cancelled || isAiSearchDomainSettled(response?.data?.results),
        (response) => {
            if (entry.cancelled) return; // 취소된 폴링의 늦은 응답이 최신 상태를 덮어쓰지 않도록
            dispatch({
                type: GET_LIST_REGISTERED_DOMAIN_SUCCESS_V3,
                payload: { domain, data: response.data.results },
            });
            if (isAiSearchDomainSettled(response.data.results)) domainPolling[domain] = null;
        },
        (error) => {
            console.error("AI search domain polling failed", error);
            domainPolling[domain] = null; // 폴링 실패 시에도 settled 경로와 동일하게 엔트리 정리
        },
        SETTLE_POLL_INTERVAL_MS,
    );
    entry.instance.run();
};

// poll=false: 동기화 미완료여도 폴링하지 않고 1회 조회만(목록 페이지 - 세션 중 버튼이 갑자기 노출되는 것 방지)
export const listRegisteredDomains = (domain, externalId, { poll = true } = {}) => async (dispatch, getState) => {
    const { stage, project } = getState();

    // 같은 도메인 폴링을 먼저 중지: 늦게 도착하는 폴링 응답이 이 fetch 결과를 덮어쓰지 않도록
    dispatch(stopRegisteredDomainsPolling(domain));
    dispatch({ type: GET_LIST_REGISTERED_DOMAIN_PENDING_V3, payload: { domain } });

    try {
        const response = await listRegisteredDomainsAPI(stage.endpoint, project.id, { domain, externalId });
        dispatch({
            type: GET_LIST_REGISTERED_DOMAIN_SUCCESS_V3,
            payload: { domain, data: response.data.results },
        });
        if (poll && !isAiSearchDomainSettled(response.data.results)) {
            dispatch(pollRegisteredDomainsUntilSettled(domain, externalId));
        }
        return response.data.results;
    } catch (error) {
        dispatch({
            type: GET_LIST_REGISTERED_DOMAIN_FAILURE_V3,
            payload: { domain, data: error },
        });
        throw error;
    }
};

export const addRegisteredDomains = (domain) => async (dispatch, getState) => {
    const { stage, project } = getState();

    try {
        const response = await registerDomainAPI(stage.endpoint, project.id, domain);
        dispatch({
            type: ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V3,
            payload: { domain, data: response.data },
        });
        dispatch(pollRegisteredDomainsUntilSettled(domain, project.id));
        return response.data;
    } catch (error) {
        dispatch({
            type: ADD_LIST_REGISTERED_DOMAIN_FAILURE_V3,
            payload: { domain, data: error },
        });
        throw error;
    }
};

export const deleteRegisteredDomains = (domain, domainId) => async (dispatch, getState) => {
    const { stage, project } = getState();

    try {
        const response = await unregisterDomainAPI(stage.endpoint, project.id, domainId);
        dispatch({
            type: DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V3,
            payload: { domain, data: domainId },
        });
        dispatch(pollRegisteredDomainsUntilSettled(domain, project.id));
        return response.data;
    } catch (error) {
        console.error(error);
        dispatch({
            type: DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V3,
            payload: { domain, data: error },
        });
        throw error;
    }
};
